import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock,Coffee, DollarSign, MapPin, Users, TrendingUp, Download, Filter } from 'lucide-react';
import AttendanceWidget from '../components/AttendanceWidget';
import apiService from '../services/api';

const Attendance = () => {
  const { t } = useTranslation();
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [adminDailyData, setAdminDailyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Get current user from localStorage or context
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    
    fetchData();
  }, [selectedDate, dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch attendance history
      const historyResponse = await apiService.getAttendanceHistory({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: 50
      });
      
      setAttendanceHistory(historyResponse.attendance || []);
      setStats(historyResponse.summary);

      // If admin, fetch daily attendance for all employees
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.role === 'admin') {
        const adminResponse = await apiService.getAdminDailyAttendance(selectedDate);
        setAdminDailyData(adminResponse);
      }

    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
     const formattedDate= new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    return formattedDate.replace(',', '.');
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours || 0);
    const m = Math.round(((hours || 0) - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'checked-in': return 'bg-green-100 text-green-800';
      case 'on-break': return 'bg-yellow-100 text-yellow-800';
      case 'checked-out': return 'bg-blue-100 text-blue-800';
      case 'incomplete': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInitials = (name) => {
    if (!name) return 'NA';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2); // Limit to 2 characters
  };

  const getAvatarColor = (name) => {
    if (!name) return 'bg-gray-500';
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 
      'bg-indigo-500', 'bg-red-500', 'bg-yellow-500', 'bg-teal-500'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const exportToCSV = () => {
    if (!attendanceHistory.length) return;

    const headers = [t('attendance.table.employee'), t('attendance.table.date'), t('attendance.table.checkIn'), t('attendance.table.checkOut'), t('attendance.table.totalHours'), t('attendance.table.breakTime'), t('attendance.table.grossPay'), t('attendance.table.status')];
    const csvContent = [
      headers.join(','),
      ...attendanceHistory.map(record => [
        record.employee ? record.employee.name : 'Unknown',
        formatDate(record.date),
        record.checkIn ? formatTime(record.checkIn.timestamp) : 'N/A',
        record.checkOut ? formatTime(record.checkOut.timestamp) : 'N/A',
        formatHours(record.totalHours),
        record.totalBreakTime || 0,
        `$${(record.grossPay || 0).toFixed(2)}`,
        record.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('attendance.title')}</h1>
          <p className="mt-2 text-gray-600">{t('attendance.subtitle')}</p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Attendance Widget */}
          <div className="lg:col-span-1">
            <AttendanceWidget />

            {/* Quick Stats */}
            {stats && (
              <div className="mt-6 bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('attendance.periodSummary')}</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('attendance.totalDays')}</span>
                    <span className="font-semibold">{stats.totalDays}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('attendance.totalHours')}</span>
                    <span className="font-semibold">{formatHours(stats.totalHours)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('attendance.avgHoursPerDay')}</span>
                    <span className="font-semibold">{formatHours(stats.avgHoursPerDay)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('attendance.totalEarnings')}</span>
                    <span className="font-semibold text-green-600">${(stats.totalGrossPay || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - History and Admin View */}
          <div className="lg:col-span-2 space-y-6">
            {/* Admin Daily Overview */}
            {currentUser?.role === 'admin' && adminDailyData && (
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{t('attendance.dailyOverview')}</h3>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 text-blue-600 mr-2" />
                        <div>
                          <div className="text-sm text-blue-600">{t('attendance.admin.totalEmployees')}</div>
                          <div className="text-lg font-bold text-blue-900">{adminDailyData.summary.totalEmployees}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-green-600 mr-2" />
                        <div>
                          <div className="text-sm text-green-600">{t('attendance.admin.checkedIn')}</div>
                          <div className="text-lg font-bold text-green-900">{adminDailyData.summary.checkedIn}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Coffee className="h-5 w-5 text-yellow-600 mr-2" />
                        <div>
                          <div className="text-sm text-yellow-600">{t('attendance.admin.onBreak')}</div>
                          <div className="text-lg font-bold text-yellow-900">{adminDailyData.summary.onBreak}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <DollarSign className="h-5 w-5 text-purple-600 mr-2" />
                        <div>
                          <div className="text-sm text-purple-600">{t('attendance.admin.totalPay')}</div>
                          <div className="text-lg font-bold text-purple-900">${(adminDailyData.summary.totalGrossPay || 0).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Employee List */}
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.employee')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.status')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.hours')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.earnings')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {adminDailyData.employees.map((emp) => (
                          <tr key={emp.employee._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div 
                                  className={`h-8 w-8 rounded-full ${getAvatarColor(emp.employee.name)} flex items-center justify-center text-white text-sm font-medium mr-3`}
                                  title={emp.employee.name}
                                >
                                  {getInitials(emp.employee.name)}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{emp.employee.name}</div>
                                  <div className="text-sm text-gray-500">{emp.employee.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(emp.status)}`}>
                                {emp.status.replace('-', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatHours(emp.totalHours)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                              ${(emp.grossPay || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Attendance History */}
            <div className="bg-white rounded-lg shadow-sm border text-gray-900">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">{t('attendance.history')}</h3>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                      <span className="text-gray-500">{t('attendance.to')}</span>
                      <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <button
                      onClick={exportToCSV}
                      className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      {t('attendance.export')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : attendanceHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('attendance.noRecords')}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.employee')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.date')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.checkIn')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.checkOut')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.hours')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.break')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.earnings')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('attendance.table.status')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {attendanceHistory.map((record) => (
                          <tr key={record._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {record.employee ? (
                                <div className="flex items-center">
                                  <div 
                                    className={`h-8 w-8 rounded-full ${getAvatarColor(record.employee.name)} flex items-center justify-center text-white text-sm font-medium mr-3`}
                                    title={record.employee.name}
                                  >
                                    {getInitials(record.employee.name)}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{record.employee.name}</div>
                                    <div className="text-sm text-gray-500">{record.employee.email}</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <div className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-sm font-medium mr-3">
                                    NA
                                  </div>
                                  <div className="text-sm text-gray-500">{t('attendance.unknown')}</div>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatDate(record.date)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.checkIn ? formatTime(record.checkIn.timestamp) : t('attendance.notAvailable')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.checkOut ? formatTime(record.checkOut.timestamp) : t('attendance.notAvailable')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatHours(record.totalHours)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {record.totalBreakTime || 0}m
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                              ${(record.grossPay || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                                {record.status.replace('-', ' ')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attendance;